import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { BaseQLMiddleware } from '@adornis/baseql/metadata/types.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { loadRawEntityDataByQuery } from '@adornis/bh-util/util.js';
import { FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { Language } from '@adornis/translation-core/languages.js';
import { validate } from '@adornis/validation/decorators.js';
import { and } from '@adornis/validation/functions/and.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { min } from '@adornis/validation/functions/range.js';
import { format, parse } from 'date-fns';
import { getDbPluginForScope } from '../db-plugins.js';
import { ParticipationState } from '../enums/enums.js';
import { mongo } from '../server/mongo.js';
import { isDefined, parseBerlinDateTime, toBerlinDate } from '../util.js';
import { Appointment } from './appointment.js';
import { Participation } from './Participation.js';
import { Product } from './products.js';
import { ProductWaitinglistPriorityRule } from './ProductWaitinglistPriorityRule.js';
import { TextMultipleLangItem } from './TextMultipleLangItem.js';
import { WorkshopDocument } from './WorkshopDocument.js';
import { WorkshopNote } from './WorkshopNote.js';
import { WorkshopSet } from './WorkshopSet.js';
import { readEntitiesByIDs } from '../server/entity-reads.js';

const workshopAppointmentsMiddleware: BaseQLMiddleware = async ({ src, context, next }) => {
  const appointments = await next();
  const { mayViewWorkshopMeetingLinks, stripAppointmentMeetingLinks } = await import('../server/meeting-link-access.js');
  return await mayViewWorkshopMeetingLinks(context, src as Workshop)
    ? appointments
    : stripAppointmentMeetingLinks(appointments);
};

const paymentExportAppointmentsMiddleware: BaseQLMiddleware = async ({ src, context, next }) => {
  const appointments = await next();
  const { mayViewWorkshopMeetingLinks, stripAppointmentMeetingLinks } = await import('../server/meeting-link-access.js');
  const product = (src as ParticipationPaymentDataExportCSV).product;
  return await mayViewWorkshopMeetingLinks(context, product ?? {})
    ? appointments
    : stripAppointmentMeetingLinks(appointments);
};

@Entity()
export class MaybeDateTimeFieldMappingEntry extends FieldMappingEntry {
  static override _class = 'MaybeDateFieldMappingEntry';

  override deserialize = v => parseBerlinDateTime(v);

  override serialize = (v: any) => {
    if (!v) return '';
    return format(toBerlinDate(new Date(v)), 'dd.MM.yyyy HH:mm');
  };
}

@Entity()
export class LenientBooleanFieldMappingEntry extends FieldMappingEntry {
  static override _class = 'LenientBooleanFieldMappingEntry';

  override deserialize = v => {
    const value = v?.trim().toLowerCase() ?? '';
    if (!value) return null;
    if (['true', 'wahr', '1', 'ja', 'yes'].includes(value)) return true;
    if (['false', 'falsch', '0', 'nein', 'no'].includes(value)) return false;
    throw new Error(`Invalid boolean: ${v}`);
  };
}

@Entity()
export class MaybeStringFieldMappingEntry extends FieldMappingEntry {
  static override _class = 'MaybeStringFieldMappingEntry';

  private defaultValue: string | null = null;

  constructor(doc: PartialEntityData<FieldMappingEntry>, defaultValue?: string) {
    super(doc);
    if (defaultValue) this.defaultValue = defaultValue;
  }

  override deserialize = v => {
    if (v === '') return this.defaultValue;
    return v + '';
  };
}

@Entity()
export class DateFieldMapping extends FieldMappingEntry {
  static override _class = 'DateFieldMapping';

  override deserialize = v => {
    if (!v) return null;
    if (Array.isArray(v)) return v.map(v => parse(v, 'dd.MM.yyyy', new Date())).join(', ');
    return parse(v, 'dd.MM.yyyy', new Date());
  };

  override serialize = (v: any) => {
    if (!v) return '';
    return format(v, 'dd.MM.yyyy');
  };
}

@Entity()
export class AppointmentDateFieldMapping extends FieldMappingEntry {
  static override _class = 'AppointmentDateFieldMapping';

  override deserialize = v => {
    if (!v) return null;
  };

  override serialize = (v: any) => {
    if (!v) return '';
    return v.map(v => format(v.start, 'dd.MM.yyyy')).join(', ');
  };
}

@Entity()
export class PriceFieldMapping extends FieldMappingEntry {
  static override _class = 'DateFieldMapping';

  override serialize = (v: any) => {
    if (!v) return '';
    return (v / 100).toFixed(2).replace('.', ',') + '€';
  };
}

/**
 * needs special handling if more than one need to be included for one field.
 * Use like this while importing
 * ```
 * new DescriptionFieldMapping({
  input: 'descriptionGermanHTML',
  output: `translatedDescriptionHTML`,
  language: Language.GERMAN,
}),
new DescriptionFieldMapping({
  input: 'descriptionEnglishHTML',
  output: `translatedDescriptionHTML_${Language.ENGLISH}`,
  language: Language.ENGLISH,
}),
// any further langauge need ths _language in output
 * ```
 * 
 * And use like this while exporting
 * ```
 * new DescriptionFieldMapping({
  input: 'descriptionGermanHTML',
  output: 'translatedDescriptionHTML',
  language: Language.GERMAN,
}),
new DescriptionFieldMapping({
  input: 'descriptionEnglishHTML',
  output: 'translatedDescriptionHTML',
  language: Language.ENGLISH,
}),
 * ```
 */
@Entity()
export class DescriptionFieldMapping extends FieldMappingEntry {
  static override _class = 'DescriptionFieldMapping';

  @Field(type => String) language!: Language;

  constructor(doc: PartialEntityData<DescriptionFieldMapping>) {
    super(doc);
  }

  override serialize = (v: TextMultipleLangItem[]) => {
    if (!v || !Array.isArray(v)) return '';
    return v.find(langText => langText.langKey === this.language)?.text ?? '';
  };

  override deserialize = (v: string | undefined, data: Record<string, any>, i: number, entityInitializer: any) => {
    if (!this.output || !v || !this.language) return null;
    const output = this.output.replace(`_${this.language}`, '');
    if (!data[output] || !Array.isArray(data[output])) data[output] = [];
    data[output].push(new TextMultipleLangItem({ text: v, langKey: this.language }).toJSON());
    return data[output];
  };
}

@Entity()
export class ParticipationPaymentDataExportCSV extends AdornisEntity {
  static override _class = 'ParticipationPaymentDataExportCSV';

  @Field(type => String) name!: string;
  @Field(type => String) price!: string;
  @Field(type => Product) product!: Product;
  @Field(type => [Appointment], { middlewares: [paymentExportAppointmentsMiddleware] }) appointments!: Appointment[];
  @Field(type => String) invoiceReceiver!: string;
  @Field(type => String) costCenter!: string;
  @Field(type => String) costCenterReceiver!: string;
  @Field(type => String) kostenart!: string;
  @Field(type => Date) createdAt!: Date;
  @Field(type => String) internalComments!: string;
}

@Entity()
export class WorkshopAffiliation extends AdornisEntity {
  static override _class = 'WorkshopAffiliation';
  @Field(type => String) affiliation!: string; // this is a class name
  @Field(type => String) affiliationID!: string;
}

@Entity()
export class Workshop extends Product {
  static override _class = 'Workshop' as const;

  @Field(type => WorkshopAffiliation)
  affiliation: Maybe<WorkshopAffiliation>;

  @Dependencies({
    classes: () => [WorkshopSet],
    ids: async function (this: PartialEntityData<Workshop>) {
      const affiliationID = this.affiliation?.affiliationID;
      if (affiliationID) return [affiliationID];
      else return [];
    },
  })
  @Field(type => String, {
    resolve: function (this: Workshop) {
      return async () => {
        if (!this.affiliation || this.affiliation.affiliation !== WorkshopSet._class) return null;
        const fields = { name: 1 } as const;
        const [workshopSet] = await readEntitiesByIDs<typeof WorkshopSet, typeof fields>(
          WorkshopSet,
          [this.affiliation.affiliationID],
          fields,
        );
        return workshopSet?.name;
      };
    },
  })
  workshopSetName: Maybe<string>;

  @Field(type => [Appointment], { default: v => v ?? [], middlewares: [workshopAppointmentsMiddleware] })
  appointments!: Appointment[];

  @Dependencies({
    classes: () => [Workshop],
    ids: function (this: PartialEntityData<Workshop>) {
      return [this._id!];
    },
  })
  @Field(type => Date, {
    resolve: function (this: Workshop) {
      return async () => {
        if (this.startDate !== null && this.startDate !== undefined) return this.startDate;
        const fields = { appointments: { start: 1 } } as const;
        const [workshop] = await readEntitiesByIDs<typeof Workshop, typeof fields>(Workshop, [this._id], fields);
        return workshop?.appointments[0]?.start;
      };
    },
  })
  startDate: Maybe<Date>;

  @validate(and(nonOptional(), min(0)))
  @Field(type => Int, { default: v => v ?? 14 })
  capacity!: number;

  /** Arbeitseinheiten / credit points, e.g. "8 AE". */
  @Field(type => String) credits: Maybe<string>;

  @Dependencies({
    classes: () => [Participation],
    ids: async function (this: PartialEntityData<Workshop>) {
      const participations = await loadRawEntityDataByQuery(Participation, { productID: this._id }, { _id: 1 });
      return participations.map(p => p._id);
    },
  })
  @Field(type => Int, {
    resolve: function (this: Workshop) {
      return async () => {
        const { calculateWorkshopLeftCapacity } = await import('../api/workshop.js');
        return calculateWorkshopLeftCapacity(this);
      };
    },
  })
  leftCapacity!: number;

  @Dependencies({
    classes: () => [Participation],
    ids: async function (this: PartialEntityData<Participation>) {
      const participations = await loadRawEntityDataByQuery(
        Participation,
        { productID: this._id, state: { $in: [ParticipationState.in, ParticipationState.participated] } },
        { _id: 1 },
      );
      return participations.map(p => p._id);
    },
  })
  @Field(type => Int, {
    resolve: function (this: Workshop) {
      return async () => {
        if (this.highPriorityParticipationCount !== null && this.highPriorityParticipationCount !== undefined)
          return this.highPriorityParticipationCount;

        const c = mongo().collection(Participation._collectionName); // ignore publish filter
        return (
          (await c.countDocuments({
            productID: this._id,
            state: { $in: [ParticipationState.in, ParticipationState.participated] },
            nachrueckPriority: 'high',
          })) ?? 0
        );
      };
    },
  })
  highPriorityParticipationCount!: number;

  @Dependencies({
    classes: () => [Participation],
    ids: async function (this: PartialEntityData<Workshop>) {
      const participations = await loadRawEntityDataByQuery(Participation, { productID: this._id }, { _id: 1 });
      return participations.map(p => p._id);
    },
  })
  @Field(type => Int, {
    resolve: function (this: Workshop) {
      return async () => {
        const m = await import('../api/participation.js');
        return await m.countWaitingByWorkshopServer(this._id);
      };
    },
  })
  waitingParticipantsCount!: number;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  hideCapacity!: boolean;

  @Dependencies({
    classes: () => [ProductWaitinglistPriorityRule],
    ids: async function (this: PartialEntityData<Participation>) {
      const rules = await loadRawEntityDataByQuery(ProductWaitinglistPriorityRule, { productID: this._id }, { _id: 1 });
      return rules.map(p => p._id);
    },
  })
  @Field(type => Int, {
    resolve: function (this: Workshop) {
      return async () => {
        if (this.nachrueckBlockTimeForLowPriorityInDays) return this.nachrueckBlockTimeForLowPriorityInDays;
        const priorityPlugin = getDbPluginForScope(
          'registration::product-waitinglist-priority-rules',
          this.institutionID,
        );
        return priorityPlugin.blockTimeForLowPriorityInDays;
      };
    },
  })
  nachrueckBlockTimeForLowPriorityInDays: Maybe<number>;

  @DataLoadedField(type => [WorkshopDocument], ({ _id }) => ({ workshopID: _id }))
  documents?: WorkshopDocument[];

  @DataLoadedField(type => [WorkshopNote], ({ _id }) => ({ workshopID: _id }))
  notes?: WorkshopNote[];

  get dateIsToBeDetermined() {
    for (const appointment of this.appointments) {
      if (!appointment.start) return true;
    }
    return false;
  }

  get start() {
    if (this.dateIsToBeDetermined) return null;
    const someStartingValue = this.appointments[0]?.start;
    if (!someStartingValue) return null;

    return this.appointments
      .map(ap => ap.start)
      .filter(isDefined)
      .reduce((value, current) => (value > current ? current : value), someStartingValue);
  }

  get end() {
    if (this.dateIsToBeDetermined) return null;
    const someStartingValue = this.appointments[0]?.end;
    if (!someStartingValue) return null;

    return this.appointments
      .map(ap => ap.end)
      .filter(isDefined)
      .reduce((value, current) => (value < current ? current : value), someStartingValue);
  }

  override toString() {
    return this.name;
  }
}
